import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group block bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-300"
      >
        <div className="grid grid-cols-1 lg:grid-cols-5">
          <div className="lg:col-span-2 relative h-48 lg:h-full min-h-[200px] overflow-hidden">
            <img
              src={article.imageUrl}
              alt={article.title}
              title={article.title}
              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
            />
          </div>
          <div className="lg:col-span-3 p-6 md:p-8 flex flex-col justify-center">
            <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-3 w-fit">
              {article.categoryName}
            </span>
            <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 mb-2">
              {article.title}
            </h3>
            <p className="text-foreground-600 text-sm leading-relaxed mb-4">
              {article.excerpt}
            </p>
            <div className="flex items-center gap-4 text-xs text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                {article.readTime}
              </span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex gap-4 p-3 rounded-lg hover:bg-background-100 transition-colors duration-200"
      >
        <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden">
          <img
            src={article.imageUrlCompact}
            alt={article.title}
            title={article.title}
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2 mb-1">
            {article.title}
          </h4>
          <div className="flex items-center gap-3 text-xs text-foreground-500">
            <span>{article.date}</span>
            <span>{article.readTime}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group block bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-300"
    >
      <div className="relative h-48 overflow-hidden">
        <img
          src={article.imageUrl}
          alt={article.title}
          title={article.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <span className="absolute top-3 left-3 inline-block px-3 py-1 rounded-full bg-background-50/90 text-primary-700 text-xs font-medium">
          {article.categoryName}
        </span>
      </div>
      <div className="p-5">
        <h3 className="font-heading text-base font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 mb-2 line-clamp-2">
          {article.title}
        </h3>
        <p className="text-foreground-600 text-sm leading-relaxed mb-4 line-clamp-2">
          {article.excerpt}
        </p>
        <div className="flex items-center gap-4 text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-calendar-line"></i>
            {article.date}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-time-line"></i>
            {article.readTime}
          </span>
        </div>
      </div>
    </Link>
  );
}